import { useEffect } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PageShell, CtaButton } from "@/components/site/Site";
import { api, useContent, usePayConfig, regionPrices } from "@/lib/api";


const HIERARCHY = [
  ["First letter", "How you meet the world — the opening note of your name."],
  ["First vowel", "Often read as an inner, core quality rather than an outward one."],
  ["Final letter", "How you tend to finish, close and follow through."],
  ["Combinations", "Clusters and vowel groups that modify the letters within them."],
  ["Middle & family names", "Supporting influences that colour the first name."],
];
const STEPS = [
  ["Enter your name", "Exactly as you use it — full first name or initials."],
  ["Confirm the structure", "Check spelling and which parts are first, middle and family names."],
  ["Pay securely", "Razorpay for India, PayPal internationally."],
  ["Receive your report", "A personalised A4 PDF delivered to your inbox, usually in less than 24 hours of your order."],
];
const THEMES = ["Personality", "Strengths", "Challenges", "Communication", "Learning style", "Relationships", "Personal growth", "Middle & family name influences"];

export default function Landing() {
  const { data } = useContent();
  useEffect(() => { api.post("/track", { path: "/" }).catch(() => {}); }, []);
  const { data: cfg } = usePayConfig();
  const prices = regionPrices((data?.prices || []).filter((p) => p.active), cfg);
  return (
    <PageShell>
      <section className="grain relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl gap-16 px-5 pb-28 pt-16 sm:px-8 lg:grid-cols-12 lg:pt-24">
          <div className="fade-up lg:col-span-6 lg:pt-10">
            <p className="font-mono-ui text-xs uppercase tracking-[0.3em] text-[#8B5A2B]" data-testid="hero-eyebrow">The Art of Namonics™</p>
            <h1 className="mt-6 font-serif-display text-5xl font-normal leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl" data-testid="hero-heading">Discover Namonics™</h1>
            <p className="mt-6 max-w-xl font-serif-display text-2xl italic leading-snug text-[#5B2C36] sm:text-3xl" data-testid="hero-slogan">Your Name Has More to Say Than You Think.</p>
            <p className="mt-8 max-w-lg text-base leading-relaxed text-stone-600 sm:text-lg" data-testid="hero-intro">{data?.hero_intro}</p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <CtaButton testId="hero-explore-btn" />
              <a href="#how" className="text-sm text-stone-600 underline decoration-[#B8860B]/50 underline-offset-8 hover:text-stone-900" data-testid="hero-how-link">How it works</a>
            </div>
            {data?.promo_message && <p className="mt-8 inline-block border-l-2 border-[#B8860B] pl-4 text-sm text-stone-700" data-testid="promo-message">{data.promo_message}</p>}
          </div>
          <div className="fade-up relative lg:col-span-6" style={{ animationDelay: "0.15s" }}>
            <div className="relative ml-auto aspect-square w-full max-w-md overflow-visible">
              <img src="/namonics-logo.png" alt="Namonics™ — Your Name Has More to Say Than You Think." className="h-full w-full object-contain drop-shadow-[0_30px_50px_rgba(232,130,12,0.18)]" data-testid="hero-logo" />
            </div>
          </div>
        </div>
      </section>

      <section id="method" className="border-t border-[#E6DEC8] py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="font-mono-ui text-xs uppercase tracking-[0.3em] text-[#8B5A2B]">The Method</p>
            <h2 className="mt-5 font-serif-display text-4xl leading-tight sm:text-5xl">Not every letter speaks with the same voice.</h2>
            <p className="mt-6 text-base leading-relaxed text-stone-600" data-testid="about-method">{data?.about_method}</p>
          </div>
          <ol className="divide-y divide-[#E6DEC8] border-y border-[#E6DEC8] lg:col-span-6 lg:col-start-7">
            {HIERARCHY.map(([t, d], i) => (
              <li key={t} className="group flex gap-8 py-7 transition-colors hover:bg-[#F6F2EA]/60">
                <span className="font-mono-ui text-xs text-[#B8860B]">0{i + 1}</span>
                <div>
                  <h3 className="font-serif-display text-2xl">{t}</h3>
                  <p className="mt-1 text-sm text-stone-600">{d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="how" className="bg-[#F6F2EA] py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <h2 className="max-w-xl font-serif-display text-4xl leading-tight sm:text-5xl">From your name to your report, in four quiet steps.</h2>
          <div className="mt-16 grid gap-px bg-[#E6DEC8] sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map(([t, d], i) => (
              <div key={t} className="bg-[#F6F2EA] p-8 transition-colors hover:bg-white" data-testid={`how-step-${i + 1}`}>
                <span className="font-serif-display text-5xl text-[#B8860B]/70">{i + 1}</span>
                <h3 className="mt-6 text-lg font-semibold">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-stone-600">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="font-mono-ui text-xs uppercase tracking-[0.3em] text-[#8B5A2B]">Inside your report</p>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl">One integrated reading.</h2>
            <div className="mt-10 flex flex-wrap gap-3">
              {THEMES.map((t) => <span key={t} className="rounded-full border border-[#E6DEC8] px-4 py-2 text-sm text-stone-700">{t}</span>)}
            </div>
            <p className="mt-8 text-sm text-stone-500">Sections appear only where your name supports them. Namonics™ is an interpretive service for personal reflection, not a scientific assessment.</p>
          </div>
          <div className="rounded-sm border border-stone-900 bg-white p-10 lg:col-span-4 lg:col-start-9" data-testid="pricing-card">
            <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-stone-500">Personal report</p>
            <div className="mt-6 space-y-2">
              {prices.map((p) => (
                <p key={p.currency} className="font-serif-display text-4xl" data-testid={`price-${p.currency}`}>
                  {p.currency} {p.amount} <span className="text-sm text-stone-500 font-sans">via {p.provider === "razorpay" ? "Razorpay" : "PayPal"}</span>
                </p>
              ))}
            </div>
            <ul className="mt-8 space-y-2 text-sm text-stone-600">
              <li>A4 PDF, designed for screen and print</li>
              <li>Delivered by email with a secure download link</li>
              <li>Support: support@whenthetimeisripe.com</li>
            </ul>
            <CtaButton testId="pricing-explore-btn" className="mt-10 w-full justify-center" />
          </div>
        </div>
      </section>

      <section className="border-t border-[#E6DEC8] py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12">
          <h2 className="font-serif-display text-4xl lg:col-span-4">Questions</h2>
          <Accordion type="single" collapsible className="lg:col-span-7 lg:col-start-6" data-testid="landing-faq">
            {(data?.faqs || []).slice(0, 5).map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} className="border-[#E6DEC8]">
                <AccordionTrigger className="text-left text-base font-medium hover:no-underline" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-stone-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="bg-stone-900 py-24 text-[#FDFBF7]">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-10 px-5 sm:px-8 md:flex-row md:items-end">
          <h2 className="max-w-2xl font-serif-display text-4xl italic leading-tight sm:text-5xl">Your Name Has More to Say Than You Think.</h2>
          <a href="/explore" data-testid="final-explore-btn" className="group inline-flex items-center gap-3 rounded-full bg-[#FDFBF7] px-7 py-3.5 text-sm font-medium text-stone-900 transition-colors hover:bg-[#D4AF37]">
            Explore Your Name <span className="transition-transform group-hover:translate-x-1">→</span>
          </a>
        </div>
      </section>
    </PageShell>
  );
}
